import {
  FiHome,
  FiFileText,
  FiMail,
  FiClipboard,
  FiLogOut,
  FiGlobe,
  FiUsers,
  FiStar,
  FiLayers,
  FiTrendingUp,
  FiUserCheck,
  FiAlertCircle,
  FiMessageSquare,
  FiCalendar,
  FiChevronRight,
  // FiChevronDown, // Reserved for future dropdown menus
  FiBook,
  FiUserPlus,
  FiCheckCircle,
  FiUser,
  FiBriefcase,
  FiTarget
} from "react-icons/fi";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect, useRef } from "react";

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const { logout } = useAuth();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  // const [submenuHeights, setSubmenuHeights] = useState({}); // Reserved for future animations
  const sidebarRef = useRef(null);
  const submenuRefs = useRef({});

  const toggleSubmenu = (menu) => {
    setOpenSubmenu(openSubmenu === menu ? null : menu);
  };

  const handleSubmenuItemClick = () => {
    setOpenSubmenu(null);
  };

  const handleMainMenuClick = () => {
    setOpenSubmenu(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setOpenSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!sidebarOpen) {
      setOpenSubmenu(null);
    }
  }, [sidebarOpen]);

  useEffect(() => {
    const calculateHeights = () => {
      const heights = {};
      Object.keys(submenuRefs.current).forEach(key => {
        if (submenuRefs.current[key]) {
          heights[key] = submenuRefs.current[key].scrollHeight;
        }
      });
      // setSubmenuHeights(heights); // Reserved for future animations
    };

    calculateHeights();
    window.addEventListener('resize', calculateHeights);
    return () => window.removeEventListener('resize', calculateHeights);
  }, []);

  const SubmenuItem = ({ to, icon: Icon, children, onClick }) => (
    <li className="transform transition-all duration-300 ease-out hover:scale-105">
      <NavLink
        to={to}
        onClick={onClick}
        className={({ isActive }) =>
          `flex items-center p-3 mx-2 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 whitespace-nowrap ${
            isActive ? "bg-[#166534] text-white shadow-lg" : "hover:bg-green-700/60 hover:shadow-md text-green-100"
          }`
        }
      >
        <Icon className="h-4 w-4 transition-transform duration-300 ease-out flex-shrink-0" />
        <span className="ml-3 transition-all duration-300 ease-out text-sm">{children}</span>
      </NavLink>
    </li>
  );

  const SubmenuContainer = ({ menuKey, children, isWide = false }) => (
    <div
      className={`absolute left-full top-0 ml-2 ${isWide ? 'w-60' : 'w-48'} bg-green-800 rounded-lg shadow-2xl z-50 backdrop-blur-sm overflow-hidden`}
      style={{
        height: openSubmenu === menuKey && sidebarOpen ? 'auto' : '0px',
        width: openSubmenu === menuKey && sidebarOpen ? (isWide ? '240px' : '192px') : '0px',
        opacity: openSubmenu === menuKey && sidebarOpen ? 1 : 0,
        visibility: openSubmenu === menuKey && sidebarOpen ? 'visible' : 'hidden',
        transform: openSubmenu === menuKey && sidebarOpen 
          ? 'translateX(0px) scaleX(1)' 
          : 'translateX(-20px) scaleX(0.8)',
        transformOrigin: 'left center',
        transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        filter: openSubmenu === menuKey && sidebarOpen ? 'blur(0px)' : 'blur(1px)',
      }}
    >
      <div 
        ref={el => submenuRefs.current[menuKey] = el}
        style={{
          opacity: openSubmenu === menuKey && sidebarOpen ? 1 : 0,
          transform: openSubmenu === menuKey && sidebarOpen 
            ? 'translateY(0px) scale(1)' 
            : 'translateY(-10px) scale(0.95)',
          transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
        }}
      >
        <ul className="py-2">
          {children}
        </ul>
      </div>
    </div>
  );

  return (
    <aside
      ref={sidebarRef}
      className={`${
        sidebarOpen ? "w-64" : "w-20"
      } bg-[#166534] text-white transition-all duration-300 flex flex-col relative`}
    >
      <div className="p-4 flex items-center justify-between border-b border-green-700">
        {sidebarOpen ? (
          <h1 className="text-xl font-medium">Admin Dashboard</h1>
        ) : (
          <h1 className="text-xl font-medium">AD</h1>
        )}
      </div>
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          <li>
            <NavLink
              to="/"
              onClick={handleMainMenuClick}
              className={({ isActive }) =>
                `flex items-center p-2 rounded-lg transition-all duration-400 ease-out transform hover:translate-x-1 hover:scale-105 ${
                  isActive ? "bg-green-700 text-white shadow-lg" : "hover:bg-green-700/60 hover:shadow-md"
                }`
              }
            >
              <FiHome className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && <span className="ml-3 transition-all duration-300 ease-out">Dashboard</span>}
            </NavLink>
          </li>

          {/* Publications Section */}
          <li className="relative">
            <button
              onClick={() => toggleSubmenu('publications')}
              className="flex items-center p-2 rounded-lg hover:bg-green-700/50 w-full text-left transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-md"
            >
              <FiBook className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && (
                <>
                  <span className="ml-3 transition-all duration-300 ease-out">Publications</span>
                  <div className="ml-auto transition-transform duration-500 ease-out">
                    <FiChevronRight className={`h-4 w-4 transition-transform duration-500 ease-out ${
                      openSubmenu === 'publications' ? 'rotate-90' : 'rotate-0'
                    }`} />
                  </div>
                </>
              )}
            </button>
            
            {/* Publications Submenu */}
            <SubmenuContainer menuKey="publications">
              <SubmenuItem to="/blogs" icon={FiFileText} onClick={handleSubmenuItemClick}>
                Blogs
              </SubmenuItem>
              <SubmenuItem to="/simply-smart" icon={FiStar} onClick={handleSubmenuItemClick}>
                Simply Smart
              </SubmenuItem>
              <SubmenuItem to="/globally-smart" icon={FiGlobe} onClick={handleSubmenuItemClick}>
                Globally Smart
              </SubmenuItem>
              <SubmenuItem to="/news" icon={FiAlertCircle} onClick={handleSubmenuItemClick}>
                News
              </SubmenuItem>
              <SubmenuItem to="/events" icon={FiCalendar} onClick={handleSubmenuItemClick}>
                Events
              </SubmenuItem>
            </SubmenuContainer>
          </li>

          {/* Approve Registration Section */}
          <li className="relative">
            <button
              onClick={() => toggleSubmenu('approveRegistration')}
              className="flex items-center p-2 rounded-lg hover:bg-green-700/50 w-full text-left transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-md"
            >
              <FiCheckCircle className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && (
                <>
                  <span className="ml-3 transition-all duration-300 ease-out">Approve Registration</span>
                  <div className="ml-auto transition-transform duration-500 ease-out">
                    <FiChevronRight className={`h-4 w-4 transition-transform duration-500 ease-out ${
                      openSubmenu === 'approveRegistration' ? 'rotate-90' : 'rotate-0'
                    }`} />
                  </div>
                </>
              )}
            </button>
            
            {/* Approve Registration Submenu - Made wider */}
            <SubmenuContainer menuKey="approveRegistration" isWide={true}>
              <SubmenuItem to="/university-requests" icon={FiClipboard} onClick={handleSubmenuItemClick}>
                University Requests
              </SubmenuItem>
              <SubmenuItem to="/student-requests" icon={FiUser} onClick={handleSubmenuItemClick}>
                Student Requests
              </SubmenuItem>
            </SubmenuContainer>
          </li>

          <li>
            <NavLink
              to="/contact"
              onClick={handleMainMenuClick}
              className={({ isActive }) =>
                `flex items-center p-2 rounded-lg transition-all duration-400 ease-out transform hover:translate-x-1 hover:scale-105 ${
                  isActive ? "bg-green-700 text-white shadow-lg" : "hover:bg-green-700/60 hover:shadow-md"
                }`
              }
            >
              <FiMail className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && <span className="ml-3 transition-all duration-300 ease-out">Enquiry</span>}
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/partnerships"
              onClick={handleMainMenuClick}
              className={({ isActive }) =>
                `flex items-center p-2 rounded-lg transition-all duration-400 ease-out transform hover:translate-x-1 hover:scale-105 ${
                  isActive ? "bg-green-700 text-white shadow-lg" : "hover:bg-green-700/60 hover:shadow-md"
                }`
              }
            >
              <FiTarget className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && <span className="ml-3 transition-all duration-300 ease-out">Partnerships</span>}
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/popups"
              onClick={handleMainMenuClick}
              className={({ isActive }) =>
                `flex items-center p-2 rounded-lg transition-all duration-400 ease-out transform hover:translate-x-1 hover:scale-105 ${
                  isActive ? "bg-green-700 text-white shadow-lg" : "hover:bg-green-700/60 hover:shadow-md"
                }`
              }
            >
              <FiLayers className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && <span className="ml-3 transition-all duration-300 ease-out">Popups</span>}
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/latest-updates"
              onClick={handleMainMenuClick}
              className={({ isActive }) =>
                `flex items-center p-2 rounded-lg transition-all duration-400 ease-out transform hover:translate-x-1 hover:scale-105 ${
                  isActive ? "bg-green-700 text-white shadow-lg" : "hover:bg-green-700/60 hover:shadow-md"
                }`
              }
            >
              <FiTrendingUp className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && <span className="ml-3 transition-all duration-300 ease-out">Announcements</span>}
            </NavLink>
          </li>

          {/* Mentor and Team Section */}
          <li className="relative">
            <button
              onClick={() => toggleSubmenu('mentorTeam')}
              className="flex items-center p-2 rounded-lg hover:bg-green-700/50 w-full text-left transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-md"
            >
              <FiUserPlus className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && (
                <>
                  <span className="ml-3 transition-all duration-300 ease-out">Mentor and Team</span>
                  <div className="ml-auto transition-transform duration-500 ease-out">
                    <FiChevronRight className={`h-4 w-4 transition-transform duration-500 ease-out ${
                      openSubmenu === 'mentorTeam' ? 'rotate-90' : 'rotate-0'
                    }`} />
                  </div>
                </>
              )}
            </button>
            
            {/* Mentor and Team Submenu */}
            <SubmenuContainer menuKey="mentorTeam">
              <SubmenuItem to="/mentors" icon={FiUserCheck} onClick={handleSubmenuItemClick}>
                Mentors
              </SubmenuItem>
              <SubmenuItem to="/team-members" icon={FiUsers} onClick={handleSubmenuItemClick}>
                Team Members
              </SubmenuItem>
            </SubmenuContainer>
          </li>
          <li>
            <NavLink
              to="/careers"
              onClick={handleMainMenuClick}
              className={({ isActive }) =>
                `flex items-center p-2 rounded-lg transition-all duration-400 ease-out transform hover:translate-x-1 hover:scale-105 ${
                  isActive ? "bg-green-700 text-white shadow-lg" : "hover:bg-green-700/60 hover:shadow-md"
                }`
              }
            >
              <FiBriefcase className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && <span className="ml-3 transition-all duration-300 ease-out">Careers</span>}
            </NavLink>
          </li>

          {/* Feedback Link */}
          <li>
            <NavLink
              to="/feedback"
              onClick={handleMainMenuClick}
              className={({ isActive }) =>
                `flex items-center p-2 rounded-lg transition-all duration-400 ease-out transform hover:translate-x-1 hover:scale-105 ${
                  isActive ? "bg-green-700 text-white shadow-lg" : "hover:bg-green-700/60 hover:shadow-md"
                }`
              }
            >
              <FiMessageSquare className="h-5 w-5 transition-transform duration-300 ease-out" />
              {sidebarOpen && <span className="ml-3 transition-all duration-300 ease-out">Feedback</span>}
            </NavLink>
          </li>

        </ul>
      </nav>
      <div className="fixed bottom-0 left-0 p-4 border-t border-green-700 bg-[#166534] transition-all duration-300" style={{ width: sidebarOpen ? '256px' : '80px' }}>
        <button
          onClick={logout}
          className="flex items-center p-2 rounded-lg hover:bg-green-700/50 w-full transition-all duration-300 ease-out transform hover:translate-x-1 hover:scale-105"
        >
          <FiLogOut className="h-5 w-5 transition-transform duration-300 ease-out" />
          {sidebarOpen && <span className="ml-3 transition-all duration-300 ease-out">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;